import { useEffect, useRef, useState } from "react";
import { ApiError, type PublishPayload, type RegistryCategory, type RegistryPlugin, api } from "../lib/api.ts";

interface Props {
  syncKey: string;
  isAdmin?: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  developer:    "Developer",
  productivity: "Productivity",
  ai:           "AI",
  system:       "System",
  files:        "Files",
  media:        "Media",
  utilities:    "Utilities",
  other:        "Other",
};

function fmtDate(unix: number) {
  return new Date(unix * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtDownloads(n: number) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

// ── Plugin card ───────────────────────────────────────────────────────────────

interface PluginCardProps {
  plugin: RegistryPlugin;
  isAdmin: boolean;
  syncKey: string;
  onVerified: (name: string, verified: boolean) => void;
  onFeatured: (name: string, featured: boolean) => void;
  onRemoved: (name: string) => void;
}

function PluginCard({ plugin: p, isAdmin, syncKey, onVerified, onFeatured, onRemoved }: PluginCardProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [status, setStatus] = useState("");

  function handleDownload() {
    const url = api.registry.downloadUrl(p.name);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${p.name}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setStatus("Download started — drag the .zip into Solar Agent to install");
  }

  async function handleVerify() {
    setBusy("verify");
    try {
      await api.registry.verify(syncKey, p.name, !p.verified);
      onVerified(p.name, !p.verified);
    } catch {
      setStatus("Verification failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleFeature() {
    setBusy("feature");
    try {
      await api.registry.feature(syncKey, p.name, !p.featured);
      onFeatured(p.name, !p.featured);
    } catch {
      setStatus("Feature toggle failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleRemove() {
    if (!window.confirm(`Remove "${p.name}" from the marketplace?`)) return;
    setBusy("remove");
    try {
      await api.registry.remove(syncKey, p.name);
      onRemoved(p.name);
    } catch {
      setStatus("Remove failed");
    } finally {
      setBusy(null);
    }
  }

  const tags = p.tags ? p.tags.split(",").map((t) => t.trim()).filter(Boolean) : [];

  return (
    <div className={`market-card${p.featured ? " market-card--featured" : ""}`}>
      <div className="market-card__header">
        {p.iconUrl ? (
          <img src={p.iconUrl} alt={p.name} className="market-card__icon" />
        ) : (
          <div className="market-card__icon market-card__icon--placeholder">
            {p.name[0]?.toUpperCase() ?? "?"}
          </div>
        )}
        <div className="market-card__meta">
          <div className="market-card__name-row">
            <span className="market-card__name">{p.name}</span>
            {p.verified && <span className="market-badge market-badge--haiv">✓ haiv</span>}
            {p.featured && <span className="market-badge market-badge--featured">Featured</span>}
            {p.engine === "wasm" && <span className="market-badge market-badge--wasm">WASM</span>}
          </div>
          <span className="market-card__version">v{p.version}</span>
        </div>
      </div>

      {p.description && <p className="market-card__desc">{p.description}</p>}

      <div className="market-card__tags">
        {p.category && (
          <span className="market-tag market-tag--category">
            {CATEGORY_LABELS[p.category] ?? p.category}
          </span>
        )}
        {tags.slice(0, 4).map((t) => (
          <span key={t} className="market-tag">{t}</span>
        ))}
      </div>

      <div className="market-card__footer">
        <span className="market-card__stat" title="Total downloads">
          ⬇ {fmtDownloads(p.downloadCount)}
        </span>
        <span className="market-card__stat">{fmtDate(p.publishedAt)}</span>
        <div className="market-card__actions">
          {p.gitUrl && (
            <a
              href={p.gitUrl}
              target="_blank"
              rel="noreferrer"
              className="market-btn market-btn--ghost"
              title="View source"
            >
              Source ↗
            </a>
          )}
          {p.zipUrl && (
            <button type="button" className="market-btn" onClick={handleDownload}>
              Download .hex
            </button>
          )}
        </div>
      </div>

      {status && <p className="market-card__status">{status}</p>}

      {isAdmin && (
        <div className="market-card__admin">
          <button
            type="button"
            className={`market-btn market-btn--sm ${p.verified ? "market-btn--danger" : "market-btn--verify"}`}
            onClick={handleVerify}
            disabled={busy === "verify"}
          >
            {busy === "verify" ? "…" : p.verified ? "Unverify" : "Verify (HAIV)"}
          </button>
          <button
            type="button"
            className={`market-btn market-btn--sm ${p.featured ? "market-btn--danger" : "market-btn--ghost"}`}
            onClick={handleFeature}
            disabled={busy === "feature"}
          >
            {busy === "feature" ? "…" : p.featured ? "Unfeature" : "Feature"}
          </button>
          <button
            type="button"
            className="market-btn market-btn--sm market-btn--danger"
            onClick={handleRemove}
            disabled={busy === "remove"}
          >
            {busy === "remove" ? "…" : "Remove"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Publish form ──────────────────────────────────────────────────────────────

interface PublishFormProps {
  syncKey: string;
  onPublished: () => void;
}

function PublishForm({ syncKey, onPublished }: PublishFormProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState<PublishPayload>({
    name: "",
    version: "1.0.0",
    description: "",
    gitUrl: "",
    zipUrl: "",
    iconUrl: "",
    category: "",
    tags: "",
    engine: "js",
  });

  function set(key: keyof PublishPayload, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name || !form.version || !form.gitUrl) {
      setError("Name, version, and Git URL are required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.registry.publish(syncKey, form);
      setOpen(false);
      setForm({ name: "", version: "1.0.0", description: "", gitUrl: "", zipUrl: "", iconUrl: "", category: "", tags: "", engine: "js" });
      onPublished();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Publish failed");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="market-btn" onClick={() => setOpen(true)}>
        + Publish Plugin
      </button>
    );
  }

  return (
    <form className="publish-form" onSubmit={submit}>
      <h3>Publish a Plugin</h3>
      <div className="publish-form__grid">
        <label>
          Name *
          <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="my-cool-hex" required />
        </label>
        <label>
          Version *
          <input value={form.version} onChange={(e) => set("version", e.target.value)} placeholder="1.0.0" required />
        </label>
        <label className="publish-form__full">
          Description
          <input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What does this plugin do?" />
        </label>
        <label className="publish-form__full">
          Git URL *
          <input value={form.gitUrl} onChange={(e) => set("gitUrl", e.target.value)} placeholder="https://github.com/you/my-cool-hex" required />
        </label>
        <label className="publish-form__full">
          Zip URL (direct download)
          <input value={form.zipUrl} onChange={(e) => set("zipUrl", e.target.value)} placeholder="https://cdn.example.com/my-cool-hex.zip" />
        </label>
        <label>
          Icon URL
          <input value={form.iconUrl} onChange={(e) => set("iconUrl", e.target.value)} placeholder="https://..." />
        </label>
        <label>
          Category
          <select value={form.category} onChange={(e) => set("category", e.target.value)}>
            <option value="">— None —</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </label>
        <label>
          Tags (comma-separated)
          <input value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="git, commit, automation" />
        </label>
        <label>
          Engine
          <select value={form.engine} onChange={(e) => set("engine", e.target.value)}>
            <option value="js">JS</option>
            <option value="wasm">WASM</option>
          </select>
        </label>
      </div>
      {error && <p className="publish-form__error">{error}</p>}
      <div className="publish-form__actions">
        <button type="submit" className="market-btn" disabled={busy}>{busy ? "Publishing…" : "Publish"}</button>
        <button type="button" className="market-btn market-btn--ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </form>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Marketplace({ syncKey, isAdmin = false }: Props) {
  const [plugins, setPlugins] = useState<RegistryPlugin[]>([]);
  const [categories, setCategories] = useState<RegistryCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "verified" | "featured">("all");
  const searchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function load(overrideQ?: string, overrideCat?: string, overrideFilter?: typeof activeFilter) {
    setLoading(true);
    const query = overrideQ ?? q;
    const cat = overrideCat ?? activeCategory;
    const filter = overrideFilter ?? activeFilter;
    try {
      const res = await api.registry.list(syncKey, {
        q: query || undefined,
        category: cat || undefined,
        verified: filter === "verified" ? true : undefined,
        featured: filter === "featured" ? true : undefined,
      });
      setPlugins(res.plugins);
    } catch {
      setPlugins([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadCategories() {
    try {
      const res = await api.registry.categories(syncKey);
      setCategories(res.categories);
    } catch {
      setCategories([]);
    }
  }

  useEffect(() => {
    load();
    loadCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSearch(val: string) {
    setQ(val);
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => load(val, activeCategory, activeFilter), 280);
  }

  function setCategory(cat: string) {
    setActiveCategory(cat);
    load(q, cat, activeFilter);
  }

  function setFilter(filter: typeof activeFilter) {
    setActiveFilter(filter);
    load(q, activeCategory, filter);
  }

  function onVerified(name: string, verified: boolean) {
    setPlugins((prev) => prev.map((p) => (p.name === name ? { ...p, verified } : p)));
  }

  function onFeatured(name: string, featured: boolean) {
    setPlugins((prev) => prev.map((p) => (p.name === name ? { ...p, featured } : p)));
  }

  function onRemoved(name: string) {
    setPlugins((prev) => prev.filter((p) => p.name !== name));
  }

  return (
    <div className="page market-page">
      <div className="page-header">
        <div>
          <h1>HAIV Marketplace</h1>
          <p className="market-subtitle">
            Discover and install verified Solar Agent plugins. <span className="market-badge market-badge--haiv">✓ haiv</span> plugins are reviewed by Solar Hex.
          </p>
        </div>
        <PublishForm syncKey={syncKey} onPublished={() => { load(); loadCategories(); }} />
      </div>

      <div className="market-controls">
        <input
          type="search"
          className="market-search"
          placeholder="Search plugins…"
          value={q}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <div className="market-filters">
          <button type="button" className={`market-filter-btn${activeFilter === "all" ? " is-active" : ""}`} onClick={() => setFilter("all")}>
            All
          </button>
          <button type="button" className={`market-filter-btn${activeFilter === "verified" ? " is-active" : ""}`} onClick={() => setFilter("verified")}>
            ✓ Verified
          </button>
          <button type="button" className={`market-filter-btn${activeFilter === "featured" ? " is-active" : ""}`} onClick={() => setFilter("featured")}>
            Featured
          </button>
        </div>
      </div>

      <div className="market-body">
        {categories.length > 0 && (
          <aside className="market-sidebar">
            <h3>Categories</h3>
            <button
              type="button"
              className={`market-cat-btn${activeCategory === "" ? " is-active" : ""}`}
              onClick={() => setCategory("")}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.category}
                type="button"
                className={`market-cat-btn${activeCategory === c.category ? " is-active" : ""}`}
                onClick={() => setCategory(c.category)}
              >
                {CATEGORY_LABELS[c.category] ?? c.category}
                <span className="market-cat-count">{c.count}</span>
              </button>
            ))}
          </aside>
        )}

        <section className="market-grid-wrapper">
          {loading ? (
            <div className="loading">Loading…</div>
          ) : plugins.length === 0 ? (
            <div className="empty">
              {q || activeCategory || activeFilter !== "all"
                ? "No plugins match your search."
                : "No plugins published yet. Be the first!"}
            </div>
          ) : (
            <div className="market-grid">
              {plugins.map((p) => (
                <PluginCard
                  key={p.id}
                  plugin={p}
                  isAdmin={isAdmin}
                  syncKey={syncKey}
                  onVerified={onVerified}
                  onFeatured={onFeatured}
                  onRemoved={onRemoved}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
